import {
    ChangeDetectorRef, Component, ContentChild, DestroyRef, effect, EventEmitter, inject, Input, input, isDevMode, NgZone, OnInit, Output,
    output, signal, TemplateRef, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { catchError, distinctUntilChanged, map, Observable, of, switchMap, take, tap } from 'rxjs';

import { SortMeta } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Table, TableLazyLoadEvent, TableRowSelectEvent, TableRowUnSelectEvent } from 'primeng/table';

import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import isString from 'lodash/isString';

import { HisAuthService } from '../../auth/service/his-auth.service';
import { HisSessionStorageKeys } from '../../core/model/his-session-storage-keys';
import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';
import { HisNavigationService } from '../../core/service/base/his-navigation.service';
import { HisJobStarterComponent } from '../../jobs/component/job-starter/his-job-starter.component';
import { HisColumnMetadata } from '../model/his-column-metadata';
import { HisSearchResult, HisSearchResultRow, HisSearchResultSelectedRow, SelectableRow } from '../model/his-search-result';
import { HisSearchResultRowContext } from '../model/his-search-result-row-context';
import { HisSearchService } from '../service/his-search.service';

import { HisSearchSelectionSetting } from '../model/his-search-selection-setting';
import { HisSearch, HisSearchCriteria } from '../model/search/his-search';
import { HisSearchItem } from '../model/search/his-search-item';
import { HisSearchNonCriteriaUrlParam } from '../model/search/his-search-non-criteria-url-param';
import { HisSearchParser } from '../model/search/his-search-parser';
import { HisSearchParserHelper } from '../model/search/his-search-parser-helper';
import { HisQueryParamUtils } from '../utils/his-query-param-utils';

import { HisRowsPerPageComponent } from '../../core/component/rows-per-page/his-rows-per-page.component';
import { HisFrozenColDirective } from '../../core/directive/his-frozenCol.directive';
import { HisUserProfileService } from '../../core/service/backend/his-userprofile.service';
import { DialogHeaderContext } from '../../core/service/base/his-dialog-header-paginator.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { HisStringUtils } from '../../core/utils';
import { DevTableConfigChanges, effectiveTablePageSize } from '../../table/model/table-model';
import { HisTableService } from '../../table/service/his-table.service';
import { HisSortCriteria } from '../model/his-search-request';
import { HisSearchResultColumnSortHelper } from '../model/his-search-result-column-sort-helper';
import { HisSearchPrintviewData } from '../model/printview/his-search-printview-data';
import { HisSearchResultConfigColumn } from '../model/searchresultconfig/his-search-result-config-column';
import { FilterUtils } from '../utils/filter-utils';

type Position = 'top' | 'bottom' | 'both';
const VIEW_MODE = { liste: 'Liste', tabelle: 'Tabelle' } as const;
type ViewMode = typeof VIEW_MODE[keyof typeof VIEW_MODE];

@Component( {
    selector: 'his-search-result',
    templateUrl: './his-search-result.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchResultComponent implements OnInit {
    private baseGlobalService = inject( HisBaseGlobalsService );
    private searchService = inject( HisSearchService );
    private navigationService = inject( HisNavigationService );
    private route = inject( ActivatedRoute );
    private router = inject( Router );
    authService = inject( HisAuthService );
    private notificationService = inject( HisNotificationService );
    private uiService = inject( HisUiService );
    private userProfileService = inject( HisUserProfileService );
    private readonly tableService = inject( HisTableService );

    protected idColumnIndex: number;

    readonly showList = toSignal(
        this.route.queryParamMap.pipe(
            map( p => ( p.get( 'mode' ) as ViewMode | null ) === VIEW_MODE.liste ),
            distinctUntilChanged()
        ),
        { initialValue: false }
    );

    /**
     * The pathname of 'window.location.pathname' as substitute for missing tableId.<br/>
     * It is required when saving table config items at the user profile.<br/>
     * Usage: HisStringUtils.getHashCode(this.pathname + this.searchConfigFile);
     */
    private readonly PATHNAME = window.location.pathname;

    private readonly DEFAULT_PAGE_SIZE = 10;

    @Input() searchConfigFile: string;
    @Input() pageTitle: string;
    @Input() tableTitle: string;
    @Input() showTitle = true;
    @Input() showNewSearchButton = true;
    @Input() showChangeSearchButton = true;
    @Input() jobStarterKey: string;
    doShowJobStarter = false;
    @Input() showBackButton = false;
    @Input() searchShowPrintviewButton = true;
    @Input() resultConfigFile: string;

    @Input() search: HisSearch;

    // The id of the column containing the row ids
    @Input() idColumn: string;
    // The id of the column whose entries are rendered as links
    @Input() defaultColumn: string;
    // The route taken when the default column is clicked
    @Input() defaultActionRoute: string;
    @Input() defaultActionRouteQueryParams: any = null;
    @Input() defaultActionUrl: string;
    @Input() defaultActionToolTip: string;
    @Input() defaultActionRowRight: string;

    // true if the rendered page contains both the search form and the search result
    @Input() isSearchFormResult = false;
    // true if this is a search popup. In that case we don't want to register the url for the "back to search result" button.
    @Input() isSearchPopup = false;

    // Used to determine the relevant UI elements
    protected selectionSettingEnum = HisSearchSelectionSetting;
    @Input() selectionSetting: HisSearchSelectionSetting = HisSearchSelectionSetting.NoSelect;

    // a CSS style that can be passed to the search result table
    @Input() resultTableStyle: any;
    @Input() hisIcon = 'arrow_left';

    // Specifies the heading level for the <his-heading> component.
    @Input() hierarchyLevel: number;

    //userProfileKey to store current viewMode (liste/Tabelle). If the same key is used across pages, they share the same mode
    @Input() searchResultModeUserProfileKey: string = 'searchResultMode';

    /** Row context managing rows and their selection. */
    protected readonly rowContext: HisSearchResultRowContext = new HisSearchResultRowContext();
    /** backwards compatibility for extensions accessing `rows` directly
     * @deprecated will be removed in the future; for read access to the current rows, bind/subscribe to the `rowsChanged` output */
    get rows() { return this.rowContext.rows; }
    set rows( rows: HisSearchResultRow[] ) { this.rowContext.rows = rows; }
    /** backwards compatibility for extensions accessing `selectedRows` directly
     * @deprecated will be removed in the future; for read access to the current selected rows, bind/subscribe to the `selectedRowsChanged` output */
    get selectedRows() { return this.rowContext.activePageSelection; }
    set selectedRows( rows: HisSearchResultRow[] ) { this.rowContext.activePageSelection = rows; }
    /** Initial state applied to `rowContext.allSelectedRows` on init. */
    readonly initialSelectedRows = input<HisSearchResultSelectedRow[]>();
    /** Output event for changed row selection. Subscribe for reactive update on selection changes. */
    readonly selectedRowsChanged = output<HisSearchResultSelectedRow[]>();

    /** backwards compatibility for extensions accessing `selectedOptions` directly
     * @deprecated obsolete due to `rowContext` managing selections and combining `selectedRows` and `selectedOptions` */
    @Input() selectedOptions: any[] = [];
    /** column id holding the id value to use as `HisOption.id` */
    @Input() selectedOptionsIdColumn?: string = undefined;
    /** column id holding the label value to use as `HisOption.label` */
    @Input() selectedOptionsLabelColumn?: string = undefined;
    // the number of result rows per page (is read from and written to UserProfile)
    @Input() resultRowsPerPage = this.DEFAULT_PAGE_SIZE;

    @Input() frozenCols: string;

    // for intern usage isted of resultRowsPerPage
    rowsPerPage = clone( this.resultRowsPerPage );

    /** "Tabelle anpassen" > Standard-Anzahl der Tabellenzeilen" */
    defaultTablePageSize: number;

    // the first result row to display
    firstResultRow = 0;
    // the total number of row results, not just those on the current page
    totalRowCount = 0;
    // value of URL parameter page
    private pageFromUrl: number = undefined;
    // current page, starting at 1
    currentPageNumber = 1;
    // total number of pages
    totalNumberOfPages?: number;
    // The number of result rows has been restricted by the search configuration if != null
    resultRowsLimitedMessage: string = undefined;

    // limits the number of search result rows
    maxResultRows: number = undefined;

    // metadata describing the columns of the search result
    columns: HisColumnMetadata[] = [];

    // configColumns dienen ausschließlich dazu die Checkboxen in 'Tabelle anpassen' zu steuern
    configColumns: HisSearchResultConfigColumn[] = [];

    numberOfDataColumns: number;
    numberOfVisibleDataColumns: number;

    // permits direct access to p-table attributes and methods
    @ViewChild( 'searchResultTable', { static: true } ) searchResultTable: Table;

    // permits direct access to his-search-result-rows-per-page attributes and methods
    @ViewChild( 'resultRowsPerPageComponent', { static: true } ) resultRowsPerPageComponent: HisRowsPerPageComponent;

    // search result action buttons
    @ContentChild( 'rowAction' ) rowAction: TemplateRef<{ index: number; row: HisSearchResultRow }>;

    @ContentChild( 'rowCustomIcon' ) rowCustomIcon: TemplateRef<{ index: number; row: HisSearchResultRow }>;

    // default action column action
    @ContentChild( 'defaultAction' ) defaultAction: TemplateRef<{ index: number; row: HisSearchResultRow }>;

    // search result list template, needs to be implemented where his-search-result-tag is used
    @Input() resultListTemplate: TemplateRef<any>;

    @Input() customResultRowsTemplate: TemplateRef<any>;

    queryParams: Params;

    radioButtonGroups: any = {};

    // To hide the 'no data' message until the search result has been loaded
    initialLoadFinished = false;
    @Output() finishedInitialLoad: EventEmitter<void> = new EventEmitter();

    @Output() singleSelectionSubmited: EventEmitter<any> = new EventEmitter();

    // if true, the current url will be ignored for backToSearchResultButton
    @Input() ignoreForBackToSearchResultNavigation = false;
    isNotActiveSearch = false;

    isExtended = false;
    navigationPosition: string;

    // notifying embedding components allows them to manipulate the data after it was loaded
    @Output() rowsChanged: EventEmitter<HisSearchResultRow[]> = new EventEmitter();

    @Input() showSearchInfo = true;

    @Input() showTableFilter = true;

    // TODO toggleTableFilter und onToggleFilter eventuell zu einer Variablen zusammenfassen
    toggleTableFilter = false;
    @Output() filterToggled: EventEmitter<boolean> = new EventEmitter();

    filter: any = {};

    @Input() showTableConfig = true;

    /** falls false wird kein Paginator angezeigt */
    @Input() showPaginator = true;

    paginatorPosition = signal<Position>( 'bottom' );

    paginator = this.showPaginator;

    /** Constant to name the action colum before other table colums */
    readonly ACTIONS_BEFORE = 'ActionsBefore';

    /** Constant to name the action colum after other table colums */
    readonly ACTIONS_AFTER = 'ActionsAfter';

    /** Zeilen pro Seite untere Grenze */
    minRows = 1;

    /** Maximale Anzahl von Zeile pro Seite: Zeilen pro Seite (Max:maxRows) */
    @Input() maxRows = 300;

    /** (hidden)columnId from genericSearchXml used for row tooltip */
    @Input() genericSearchRowTooltipId: string;

    @Input() rowTooltipGeneratorMethod: ( row: HisSearchResultRow ) => string;

    @Input() loadLastSortOrder = true;

    @Input() customIconsColumnHeading: string;

    tableHeaderSelectedAll = false;

    multiSortMeta: SortMeta[];

    userProfileKeyBasePath: string;
    //Properties to use on selected items overlay
    searchToUseOverlay: HisSearch;

    headingLevelPageTitle: number;
    headingLevelTableTitle: number;

    private entriesIds: any[] = []; // IDs extracted from the search results.

    @ViewChild( HisFrozenColDirective, { static: false } ) private frozenColDirective?: HisFrozenColDirective;

    private readonly destroyRef = inject( DestroyRef );
    private cdr = inject( ChangeDetectorRef );
    private zone = inject( NgZone );

    protected currentMultiSortMeta: SortMeta[] = [];

    /** linke Aktionenspalte anzeigen */
    protected showActionsBefore = true;

    /** rechte Aktionenspalte anzeigen */
    protected showActionsAfter = true;

    constructor() {
        console.log( 'HisSearchResultComponent.construct()' );

        // link selectedRowsChanged event emitter to the selectedRows; has to stay in constructor to ensure injection context
        effect( () => {
            this.selectedRowsChanged.emit( this.rowContext.selectedRows() );
        } );
    }

    applyFrozenCols(): void {
        this.cdr.detectChanges();
        this.zone.onStable.pipe(take(1)).subscribe(() => {
            this.frozenColDirective?.refresh();
        });
    }

    ngOnInit() {

        this.headingLevelPageTitle = ( this.hierarchyLevel ?? 0 ) + 1;
        this.headingLevelTableTitle = ( this.hierarchyLevel ?? 0 ) + 2;

        this.setUserProfileBasePath();

        // Remember queryParams for jump back to search page via "Suche ändern"
        this.queryParams = this.route.snapshot.queryParams;
        console.log( 'HisSearchResultComponent.ngOnInit(): queryParams=', this.queryParams );

        let isFlatObjectFormat = HisQueryParamUtils.isFlatObjectFormat( this.queryParams );

        // If we are on a simple search result page not including the search form, then
        // derive searchConfigFile, pageTitle, tableTitle and searchData from queryParams.
        if ( this.queryParams && !this.isSearchFormResult ) {
            console.log( 'Init search result with queryParams =', this.queryParams );

            let searchItems: HisSearchItem[] = [];
            for ( let key of Object.keys( this.queryParams ) ) {
                switch ( key ) {
                    // searchConfigFile, pageTitle and tableTitle etc. are taken from queryParams
                    // only when given; otherwise they may have been set via @Input
                    case HisSearchNonCriteriaUrlParam.CONFIG_FILE: this.searchConfigFile = this.queryParams.configFile; break;
                    case HisSearchNonCriteriaUrlParam.PAGE_TITLE: this.pageTitle = this.queryParams.pageTitle; break;
                    case HisSearchNonCriteriaUrlParam.TABLE_TITLE: this.tableTitle = this.queryParams.tableTitle; break;
                    case HisSearchNonCriteriaUrlParam.IS_NO_SEARCH_ACTIVE:
                        this.isNotActiveSearch = this.queryParams.isNotActiveSearch; break;
                    // showChangeSearchButton needs conversion from string to boolean.
                    // Since its default value is true, we set it to true whenever the string is not equal to 'false'.
                    case HisSearchNonCriteriaUrlParam.SHOW_CHANGE_SEARCH_BUTTON:
                        this.showChangeSearchButton = this.queryParams.showChangeSearchButton !== 'false'; break;
                    // showBackButton
                    case HisSearchNonCriteriaUrlParam.SHOW_BACK_BUTTON:
                        this.showBackButton = this.queryParams.showBackButton !== 'false'; break;
                    // page could be passed by URL by "Suche ändern"
                    case HisSearchNonCriteriaUrlParam.PAGE: {
                        // currentPageNumber muss hier evtl. nicht notwendigerweise gesetzt werden
                        this.pageFromUrl = this.currentPageNumber = Number( this.queryParams.page );
                        if ( isNaN( this.pageFromUrl ) ) {
                            this.currentPageNumber = 1;
                            this.pageFromUrl = undefined;
                        }
                        // Hier würden wir auch gern firstResultRow setzen, damit die p-table-Komponente
                        // diesen In-/Output richtig aktualisiert, event.first beim Aufruf von load()
                        // richtig setzt, und wir daraus currentPageNumber korrekt ableiten können.
                        // Das geht aber leider nicht, weil die Berechnung von firstResultRow die Variable
                        // rowsPerPage benötigt, die aus dem UserProfile geladen wird. Wenn wir das hier machen
                        // würden, dann würden wir einen ExpressionChangedAfterItHasBeenCheckedError bekommen.
                        // Also müssen wir uns in load() ein bisschen mehr Mühe geben, um all diese Variablen
                        // richtig zu setzen.
                        break;
                    }
                    case HisSearchNonCriteriaUrlParam.EXTENDED: this.isExtended = true; break;
                    // see hiszilla #259078
                    case HisSearchNonCriteriaUrlParam.NAVIGATION_POSITION:
                        this.navigationPosition = this.queryParams.navigationPosition; break;

                    // blacklisted keys are ignored, see #192323, #212551, #249099
                    case HisSearchNonCriteriaUrlParam.NO_DB_ACTION:
                    case HisSearchNonCriteriaUrlParam.INIT:
                    case HisSearchNonCriteriaUrlParam.CHOC:
                    case HisSearchNonCriteriaUrlParam.MODE: break;

                    default:
                        // All other parameters are permitted. Keys added by URL manipulation (#223241) are filtered in the backend
                        // Update: key value pairs are now transmitted as part of a flat object
                        // group0.criteria0.field = person.gender.id
                        // group0.criteria0.value = 2
                        if ( !isFlatObjectFormat ) {
                            continue;
                        }
                        searchItems.push( new HisSearchItem( key, this.queryParams[key] ) );
                }
            }

            if ( isFlatObjectFormat ) {
                console.log( 'his-search-result.component#ngOnInit()', '#is flat object url format' );
                this.search = HisSearchParser.decode( searchItems );
                this.search.configFile = this.searchConfigFile;
            } else {
                console.log( 'his-search-result.component#ngOnInit()', '#is key value url format or empty search' );
                // Es ist eine leere Suche oder eine alte URL im key value Format
                // hierfür ist eine Auswertung der Gruppen aus der Suchkonfiguration nicht notwendig,
                // deshalb ist group.label = 'default',
                // da die Gruppen nicht bekannt sind, wird für alle Suchkriterien in Search nur diese eine Gruppe erzeugt,
                // also können auch keine URL mit Suchkriterien für Kriteriengruppen ausgewertet werden.
                let newQueryParams: Params = {};
                if ( this.navigationPosition ) {
                    newQueryParams.navigationPosition = this.navigationPosition;
                }

                this.search = HisSearchParserHelper.convertOldUrlParamsToHisSearch( this.queryParams, this.searchConfigFile );
                let searchParams = HisSearchParserHelper.convertHisSearchToFlatObjectFormatParams( this.search );
                for ( let key of Object.keys( searchParams ) ) {
                    newQueryParams[key] = searchParams[key];
                }

                if ( this.isExtended ) {
                    newQueryParams.extended = true;
                }
                this.queryParams = newQueryParams;
            }
            if (this.resultConfigFile?.length) {
                this.search.resultConfigFile = this.resultConfigFile;
            }
            console.log( 'his-search-result.component#ngOnInit()', '#this.search', this.search );

            if ( isDevMode() ) {
                console.log( 'Search Config File: \'' + this.searchConfigFile + '\'' );
                console.log( 'Search Page Title: \'' + this.pageTitle + '\'' );
                console.log( 'Search Table Title: \'' + this.tableTitle + '\'' );
                console.log( 'ShowChangeSearchButton: \'' + this.showChangeSearchButton + '\'' );
                console.log( 'Search Parameters:', this.search );
            }
        }

        // If this is not a popup search then register the current url for "back to search result" button
        console.log( 'Init search result: isSearchPopup =', this.isSearchPopup );
        if ( !this.isSearchPopup ) {
            if ( isFlatObjectFormat ) {
                let url: string = window.location.href;
                if ( url.indexOf( '&page=' ) < 0 ) {
                    url += '&page=' + this.currentPageNumber.toString();
                }
                console.log(
                    'his-search-result.component#ngOnInit()',
                    '#Register search result url (flat object format)', url );
                if ( !this.isNotActiveSearch && !this.ignoreForBackToSearchResultNavigation ) {
                    this.navigationService.setSearchResultUrl( url );
                }
            } else {
                // Url in neues Format Konvertieren
                let queryParams = Object.create( this.queryParams );

                this.router.navigate( [], {
                    relativeTo: this.route,
                    queryParams,
                    queryParamsHandling: 'merge',
                    // preserve the existing query params in the route
                    skipLocationChange: true
                    // do not trigger navigation
                } );
                let url: string = window.location.href;
                if ( !this.isNotActiveSearch && !this.ignoreForBackToSearchResultNavigation ) {
                    console.log( 'his-search-result#ngOnInit()', '# !this.isNotActiveSearch', !this.isNotActiveSearch );
                    this.navigationService.setSearchResultUrl( url );
                }
                console.log( 'his-search-result#ngOnInit()', '#url', this.router.url );
            }
        }

        this.doShowJobStarter = this.showJobStarter();

        console.log( 'his-search-result.component#ngOnInit() #isAnonymous', this.authService.isAnonymous );
        console.log( 'his-search-result.component#ngOnInit() #this.rowsPerPage', this.rowsPerPage );

        this.initTableConfigUserProfileValues();

        if ( this.loadLastSortOrder ) {
            HisSearchResultColumnSortHelper.createSortCriteriaBySessionStorageValues(
                HisSearchResultColumnSortHelper.getSortedSortOrderKeyFromSessionStorage() );
        }

        // Filter-Kriterien aus URL bzw. searchParams übernehmen zum Anzeigen,
        // z. B. nach Rückkehr aus einer Detailansicht
        this.filter = { ...this.filter, ...FilterUtils.convertParamsToFilter( this.queryParams ) };

        // load selected rows from input
        if ( this.initialSelectedRows() ) {
            this.rowContext.setSelectedRows( this.initialSelectedRows() );
        }

        if ( this.showPaginator ) {
            this.loadPaginatorPosition();
        }

        this.currentMultiSortMeta = HisSearchResultColumnSortHelper.createMultiSortMetaFromQueryParams( this.queryParams );
    }

    protected toggleSelectAll() {
        this.tableHeaderSelectedAll = !this.tableHeaderSelectedAll;
    }

    getTooltipTableHeader() {
        console.log( 'getTooltipTableHeader tableHeaderSelectedAll: ', this.tableHeaderSelectedAll );
        return this.uiService.translate( 'genericSearch.selectAllOfPage' );
    }

    /** get tooltip for the entry of a given row */
    getTableCheckboxTitleForRow( row: HisSearchResultRow ) {
        /* console.log( 'his-search-result.component#getTableCheckboxTitleForRow( row: HisSearchResultRow )' +
            'rowTooltipId: ', rowTooltipId); */

        if ( this.rowContext.isRowSelectedOnPage( row.rowKey ) ) {
            return this.uiService.translate( 'common.deselectOf', this.getTooltipParam( row ) );
        } else {
            return this.uiService.translate( 'genericSearch.selectItemOf', this.getTooltipParam( row ) );
        }
    }

    getTooltipParam( row: HisSearchResultRow ) {
        return this.rowTooltipGeneratorMethod ? this.rowTooltipGeneratorMethod( row ) : row.get( this.getRowTooltipId() );
    }

    /** get genericSearchTooltipId. As default search for Id containg defaulttext or title */
    getRowTooltipId() {
        if ( this.genericSearchRowTooltipId ) {
            return this.genericSearchRowTooltipId;
        } else {
            let columnWithDefaulttextOrTitle = this.columns.find( c => c.id.includes( 'defaulttext' ) || c.id.includes( 'title' ) );
            if ( columnWithDefaulttextOrTitle ) {
                return columnWithDefaulttextOrTitle.id;
            }
        }
        return '';
    }

    initTableConfigUserProfileValues() {
        console.log(
            'his-search-result.component#initTableConfigUserProfileValues()' +
            ' #this.rowsPerPage #onEntry', this.rowsPerPage );
        console.log(
            'his-search-result.component#initTableConfigUserProfileValues()' +
            ' #this.resultRowsPerPage #onEntry', this.resultRowsPerPage );
        
        this.tableService.initTableConfig( this.userProfileKeyBasePath ).pipe( takeUntilDestroyed( this.destroyRef ) ).subscribe( config => {
            this.rowsPerPage = effectiveTablePageSize( config, this.resultRowsPerPage );
            this.firstResultRow = ( this.currentPageNumber - 1 ) * this.rowsPerPage;
    
            this.toggleTableFilter = !!config.showTableFilter;
            this.filterToggled.emit( this.toggleTableFilter );
        } );
    }

    /**
     * Select a single row from the result table.
     *
     * @param selection the selected row
     */
    doSingleSelect( selection: HisSearchResultRow ) {
        console.log( 'HisSearchResultComponent.doSingleSelect(): selection =', selection );
        console.log( 'HisSearchResultComponent.doSingleSelect(): selectedOptionsLabelColumn =', this.selectedOptionsLabelColumn );
        let id = selection.get( this.selectedOptionsIdColumn );
        // If selectedOptionsLabelColumn is undefined we take the first column of the result table.
        // See comment in HisWidgetService...
        let label = this.selectedOptionsLabelColumn ? selection.get( this.selectedOptionsLabelColumn ) : selection.getAt( 0 );
        console.log( 'Submit single-selection:', selection );
        this.singleSelectionSubmited.emit( selection.toFlatSearchResultRow( id, label ) );
    }

    /**
     * Set selectedOptions, selectedOptionsIdColumn and selectedOptionsLabelColumn.
     *
     * @param selectedOptions The new selection
     * @param selectedOptionsIdColumn The identifier of the column containing the id of a dataset
     * @param selectedOptionsLabelColumn The identifier of the column containing the label of a dataset
     * @deprecated use `setSelectedRows` instead.
     * selectedRows and selectedOptions now share a common structure to reduce complexity and duplication.
     */
    setSelectedOptions( selectedOptions: any[], selectedOptionsIdColumn: string, selectedOptionsLabelColumn: string ) {
        // calculateSelectedRows() needs this.selectedOptionsIdColumn, so we initialize the latter first
        if ( selectedOptionsIdColumn ) {
            console.log( 'HisSearchResultComponent.setSelectedOptions(): set selectedOptionsIdColumn = ' + selectedOptionsIdColumn );
            this.selectedOptionsIdColumn = selectedOptionsIdColumn;
        } else {
            console.warn( 'HisSearchResultComponent.setSelectedOptions(): selectedOptionsIdColumn is undefined!' );
        }

        console.log( 'HisSearchResultComponent.setSelectedOptions(): set selectedOptionsLabelColumn = ' + selectedOptionsLabelColumn );
        if ( selectedOptionsLabelColumn ) { this.selectedOptionsLabelColumn = selectedOptionsLabelColumn; }

        // with type any this is a wild guess; from older implementations it is assumed to either be a HisOption[] or HisSearchResultRow[]
        // if both dont match, we give up and discard the element
        if ( !selectedOptions ) {
            return;
        }

        if ( selectedOptions.length === 0 ) {
            this.rowContext.setSelectedRows( [] );
            return;
        }

        const selectedRows = this.toSelectedRows( selectedOptions );
        this.rowContext.setSelectedRows( selectedRows );

        // backwards compatibility; will be removed in the future
        this.selectedOptions = selectedOptions;
    }

    private toSelectedRows( selectedOptions: any[] ) {
        return selectedOptions.map( option => {
            if ( option instanceof HisSearchResultRow ) {
                return option.toHisSearchResultSelectedRow();
            }
            const id = this.extractOptionId( option );
            if ( id ) {
                const label = this.extractOptionLabel(option); //try to guess a label
                const selectedRow = new HisSearchResultSelectedRow( id, id, label );
                for (const key in option) {
                    if (key === 'id' || key === 'label') continue;
                    selectedRow.customData[key] = option[key];
                }
                return selectedRow;
            }
            return null;
        } ).filter( x => x !== null );
    }

    /**
     * Try to determine an id for a passed option object. If selectedOptionsIdColumn is set,
     * it is preferred to extract the id, before falling back to the `option.id` property.
     * @param option the option object to extract the id from
     * @returns an id, or undefined if no id could be resolved
     */
    private extractOptionId( option: any ): string | undefined {
        let id: string | undefined;
        if ( this.selectedOptionsIdColumn ) {
            id = option[this.selectedOptionsIdColumn];
        }
        return id ?? option.id;
    }

    /**
     * Try to determine a label for a passed option object. This might be called before the underlying
     * table data is loaded, so we have to check carefully.
     * @returns the label for the given option, or undefined if it could not be retrieved
     */
    private extractOptionLabel( option: any ): string | undefined {
        if ( this.selectedOptionsLabelColumn ) {
            return option[this.selectedOptionsLabelColumn];
        }
        if ( option.label ) {
            return option.label;
        }
        if ( this.columns && this.columns.length > 0 ) {
            return option[this.columns[0].id];
        }
        return undefined;
    }

    /**
     * Programatically set a collection of rows as selected
     * @param selectedRows the rows to set as selected
     */
    setSelectedRows( selectedRows: HisSearchResultSelectedRow[] ) {
        this.rowContext.setSelectedRows( selectedRows ?? [] );
    }

    // event.data refers to a HisSearchResultRow
    onRowSelect( event: TableRowSelectEvent ) {
        const row = event.data as HisSearchResultRow;
        const asSelectedRow = row.toHisSearchResultSelectedRow();
        this.rowContext.selectRow( asSelectedRow );
        if ( isDevMode() ) {
            console.debug( '> Selected rows: ', [...this.rowContext.selectedRowKeySet()] );
        }

        // backwards compatibility; will be removed in the future
        if ( this.selectedOptions && !this.selectedOptions.find( op => op.id == asSelectedRow.id ) ) {
            this.selectedOptions.push( asSelectedRow.toFlatHisOption() );
        }
    }

    // event.data refers to a HisSearchResultRow
    onRowUnselect( event: TableRowUnSelectEvent ) {
        const row = event.data as HisSearchResultRow;
        this.rowContext.deselectRow( row );
        if ( isDevMode() ) {
            console.debug( '> Selected rows: ', [...this.rowContext.selectedRowKeySet()] );
        }

        // backwards compatibility; will be removed in the future
        const selectedOptionId = row.toHisSearchResultSelectedRow().id;
        this.deselectRowIdFromOptions( selectedOptionId );
    }

    /**
     * Event triggered by a click in the "header checkbox" of the result table.
     *
     * @param event event
     */
    onHeaderCheckboxToggle( event: { checked: boolean } ) {
        let checked: boolean = event.checked;
        console.log( 'header checkbox toggle event: ', event, ', checked =', checked );
        if ( checked ) {
            this.rowContext.selectAllOfPage();

            // backwards compatibility; will be removed in the future
            const rows = this.rowContext.rows.map( row => row.toHisSearchResultSelectedRow() );
            const selectedRows = new Set( this.selectedOptions.map( option => String( option.id ) ) );
            const rowsToSelect = rows.filter( row => !selectedRows.has( String( row.id ) ) ).map( row => row.toFlatHisOption() );
            this.selectedOptions.push( ...rowsToSelect );
        } else {
            this.rowContext.deselectAllOfPage();

            // backwards compatibility; will be removed in the future
            const rowIdsToDeselect = this.rowContext.rows.map( row => String( row.getAt( row.idIdx ) ) );
            rowIdsToDeselect.forEach( rowId => this.deselectRowIdFromOptions( rowId ) );
        }
        if ( isDevMode() ) {
            console.debug( '> Selected rows: ', [...this.rowContext.selectedRowKeySet()] );
        }
    }

    /** @devNote DO NOT USE! This is for backwards compatibility only and will be removed */
    private deselectRowIdFromOptions( rowId: string ) {
        if ( this.selectedOptions ) {
            const idx = this.selectedOptions.findIndex( option => option.id == rowId );
            if ( idx > -1 ) {
                this.selectedOptions.splice( idx, 1 );
            }
        }
    }

    /**
     * Handles the selection of all items from the child component.
     *
     * This method is triggered when the child component his-search-result-select-buttons emits the `selectAllItems` event.
     */
    handleSelectAllItems() {
        this.searchService.loadSearchResult( this.searchToUseOverlay, 0, this.totalRowCount ).subscribe(
            ( searchResult ) => {
                const rows = searchResult.data;
                if ( !rows || rows.length === 0 ) {
                    return;
                }
                this.updateIdAndLabelColumnIdx( rows );
                this.rowContext.selectRows( rows.map( row => row.toHisSearchResultSelectedRow() ) );

                // backwards compatibility; will be removed in the future
                this.selectedOptions = this.rowContext.selectedRows().map( row => row.toFlatHisOption() );
            }
        );
    }

    /**
     * Handles the deselection of all items from the child component.
     *
     * This method is triggered when the child component his-search-result-select-buttons emits the `clearSelectedItems` event.
     */
    handleClearSelectedItems() {
        this.rowContext.deselectRows();

        // backwards compatibility; will be removed in the future
        this.selectedOptions = [];
    }

    /**
     * Updates the selected items by deselecting specific rows.
     *
     * This method is triggered when the child component his-search-result-selected-items emits the `updateSelectedItems` event.
     * It processes the rows to be deselected.
     *
     */
    handleUpdateSelectedItems( rowsToDeselect: HisSearchResultRow[] ) {
        this.deselectRows( rowsToDeselect );
    }

    /**
     * Deselects rows from the current selection.
     *
     * @param rows - An array of `SelectableRow` objects to be deselected.
     */
    private deselectRows( rows: SelectableRow[] ) {
        this.rowContext.deselectRows( rows );
        rows.forEach( row => this.deselectRowIdFromOptions( row.rowKey ) );
    }

    /**
     * Called when pagination occurs.
     * event.first is the index of first record in page.
     */
    onPage( event: PaginatorState ) {
        // adjusting the URL is not necessary when search and search result are on the same page (popup)
        console.log( 'HisSearchResultComponent.onPage(): first = ', event.first );
        this.firstResultRow = event.first;
        this.currentPageNumber = 1 + this.firstResultRow / this.rowsPerPage;

        this.queryParams = {
            ...this.queryParams,
            page: this.currentPageNumber
        };
        // page in der gespeicherten URL aktualisieren
        if ( !this.isSearchPopup ) {
            this.router.navigate( [], {
                relativeTo: this.route,
                queryParams: { page: this.currentPageNumber },
                queryParamsHandling: 'merge',
                replaceUrl: true
            } ).then( () => {
                let url = window.location.href;
                console.log( 'Register modified search result url', url );

                // fix "Zurück zum Suchergebnis" button
                if ( !this.isNotActiveSearch && !this.ignoreForBackToSearchResultNavigation ) {
                    this.navigationService.setSearchResultUrl( url );
                }
            } );
        }
    }

    /**
     * Builds a session storage key with the suffix of the column to be sorted.
     *
     * @param colField Name of the field to be sorted.
     * @returns a session storage key.
     */
    getSortCriterionSessionStorageKey( colField: string ): string {
        return HisSessionStorageKeys.CS_SYS_SORT_LAST_COLUMN_SORTORDER + '_' + colField;
    }

    load( event: TableLazyLoadEvent ) {
        console.log(
            'his-search-result.component#load event! searchConfigFile =',
            this.searchConfigFile, ', this.search =', this.search, ', event =', event );

        if ( event.multiSortMeta ) {
            this.multiSortMeta = event.multiSortMeta as SortMeta[];
            console.log( 'his-search-result.component#load() #multiSortMeta', this.multiSortMeta );
            console.log( 'his-search-result.component#load() #event', event );

            HisSearchResultColumnSortHelper.sortCriteria = [];
            let colOrder = 1;

            HisSearchResultColumnSortHelper.removeAllSessionStorageKeysContainingSortOrder();

            for ( let colToSort of this.multiSortMeta ) {
                let ascending = colToSort.order > 0 ? true : false;
                let sortCriterion = new HisSortCriteria( colToSort.field, ascending );
                console.log( 'his-search-result.component#load() #sortCriterion', sortCriterion );
                HisSearchResultColumnSortHelper.sortCriteria.push( sortCriterion );

                //Stores the sortorder in the session storage
                sessionStorage.setItem( this.getSortCriterionSessionStorageKey( colToSort.field ),
                    HisSearchResultColumnSortHelper.convert( colToSort.order ) + HisSearchResultColumnSortHelper.SPEPERATOR + colOrder );
                colOrder++;
            }
        }

        // Saves the sortCriteria in a variable named sortCriteriaToLoadInTableFilter
        // to be able to reload the sort order when filtering tables
        HisSearchResultColumnSortHelper.setSafedSortCriteria( HisSearchResultColumnSortHelper.sortCriteria );

        HisSearchResultColumnSortHelper.sortCriteria = [];

        // Since resetPageOnSort=false is set in the template, event.first should remain the current page offset on sort.
        const offset = event.first ?? this.firstResultRow ?? 0;
        this.firstResultRow = offset;
        this.currentPageNumber = 1 + this.firstResultRow / this.rowsPerPage;

        if ( !this.isSearchPopup ) {
            const urlParams = this.route.snapshot.queryParams;

            const currentMultiSortMeta = ( event.multiSortMeta as SortMeta[] ) || [];
            const currentParams = HisSearchResultColumnSortHelper.addMultiSortMetaToQueryParams( urlParams, currentMultiSortMeta );

            const hasChanged = Object.keys( currentParams ).some( key =>
                String( currentParams[key] ) !== String( urlParams[key] )
            );

            if ( hasChanged ) {
                this.updateParamsAndSetNewUrlToBrowser( currentParams );
            }
        }

        // Loads the page with stored sortCriteria
        this.loadPage( offset, undefined, event.sortField, event.sortOrder,
            HisSearchResultColumnSortHelper.getSafedSortCriteria(), FilterUtils.toFilterCrtieria( this.filter ) );
    }

    loadPage(
        start: number,
        search?: HisSearch,
        sortField?: string | string[] | null | undefined,
        sortOrder?: number,
        sortCriteria?: HisSortCriteria[],
        tableFilter: HisSearchCriteria[] = FilterUtils.toFilterCrtieria( this.filter )) {

        console.log( 'his-search-result.component#loadPage() #start', start, '#sortField', sortField, 'sortOrder', sortOrder );
        console.log( 'his-search-result.component#loadPage() #this.resultRowsPerPage', this.resultRowsPerPage );
        console.log( 'his-search-result.component#loadPage() #search #search', search );

        let searchToUse: HisSearch;
        if ( search ) {
            searchToUse = cloneDeep( search );
            this.search = cloneDeep( searchToUse );
        } else if ( this.search ) {
            searchToUse = cloneDeep( this.search );
        } else {
            if ( isDevMode() ) {
                this.notificationService.error(
                    this.uiService.translate( 'Kein gültiges HisSearch Objekt vorhanden: HisSearch = ' + searchToUse +
                        ' searchConfigFile = ' + this.searchConfigFile ),
                    this.uiService.translate( 'view.messages.error' ) );
            }
            return;
        }

        searchToUse = HisSearchParserHelper.removeDefaultGroupFromSearchIfOtherGroupIsPresent( searchToUse );
        console.log( 'his-search-result.component#loadPage() #search #searchToUse', searchToUse );

        if ( this.getTableConfig() ) {
            this.rowsPerPage = effectiveTablePageSize( this.getTableConfig(), this.resultRowsPerPage || this.DEFAULT_PAGE_SIZE );
            console.debug( 'his-search-result.component#loadPage() #this.rowsPerPage (effective from table config)', this.rowsPerPage );
        } else {
            this.rowsPerPage ??= this.resultRowsPerPage;
            console.debug( 'his-search-result.component#loadPage() #this.rowsPerPage', this.rowsPerPage );
        }

        // #265099: Für 'offset' muss der url parameter 'page' ausgewertet werden.
        // Dies kann erst hier geschehen, da zur Berechnung die pageSize benötigt wird,
        // die aus dem UserProfile geladen werden musste.
        // #287499 wurde durch Verwendung von pageFromUrl korrigiert.
        let offset: number;
        if ( this.pageFromUrl ) {
            offset = ( this.pageFromUrl - 1 ) * this.rowsPerPage;
            // pageFromUrl muss und darf nur benutzt werden wenn auch ngOnInit() ausgeführt wurde.
            // Wenn wir in der Komponente bleiben, z.B. beim Blättern, dann müssen wir event.first aus
            // p-table benutzen. Also löschen wir die Variable nach jeder Verwendung:
            this.pageFromUrl = undefined;
            console.log( 'his-search-result.component#loadPage(): offset from page URL parameter =', offset );
        } else {
            offset = start;
            console.log( 'his-search-result.component#loadPage(): offset from start =', offset );
        }

        this.configColumns = [];
        this.showActionsBefore = true;
        this.showActionsAfter = true;

        let sortStr = '';
        if ( sortField ) {
            sortStr = ' (sort: ' + sortField;
            if ( sortOrder ) {
                sortStr += ( sortOrder > 0 ) ? 'asc' : 'desc';
            }
            sortStr += ')';
        }
        console.log( 'Request loading search result rows ' + ( start + 1 ) + '..' + ( start + this.rowsPerPage )
            + sortStr + ' from \'' + this.searchConfigFile + '\' ...' );

        console.log( 'his-search-result.component#loadPage() #sortCriteria', sortCriteria );

        this.searchService.loadSearchResult( searchToUse, offset, this.rowsPerPage, sortCriteria, tableFilter ).subscribe(
            ( searchResult: HisSearchResult ) => {
                //Wenn searchResult aufgrunf von offset null ist, liegt es daran, dass die angegebene Seite größer als das Suchergebnis ist.
                //Daher findet hier eine Weiterleitung auf die erste Seite des Suchergenisses statt #287394
                this.searchToUseOverlay = searchToUse;
                if ( ( !searchResult && offset !== 0 ) || ( searchResult.limit === 0 && offset !== 0 ) ) {
                    this.searchService.loadSearchResult( searchToUse, 0, this.rowsPerPage, sortCriteria, tableFilter ).subscribe(
                        ( newSearchResult: HisSearchResult ) => {
                            if ( newSearchResult ) {
                                this.notificationService.infoI18n( 'genericSearch.notExistingPageInSearchResult', this.currentPageNumber );
                                this.createSearchResult( 1, newSearchResult, searchToUse, sortField, sortOrder );
                                this.currentPageNumber = 1;
                            }
                        } );
                } else {
                    this.createSearchResult( start, searchResult, searchToUse, sortField, sortOrder );
                }
            },
            ( error: any ) => {
                console.log( error );
            }
        );
    }

    private createSearchResult( start: number, searchResult: HisSearchResult, searchToUse?: HisSearch, sortField?: string | string[] | null | undefined, sortOrder?: number ) {
        console.log( 'start =', start, ', searchResult.offset =', searchResult.offset );
        console.log( 'rowsPerPage =', this.rowsPerPage, ', searchResult.limit =', searchResult.limit );
        console.log( 'searchResult.maxResultRows =', searchResult.maxResultRows );
        console.log( 'searchToUse', searchToUse );

        // Has the limit been reduced by the search configuration?
        if ( searchResult.maxResultRows > 0 ) {
            // show info message that resultRowsPerPage has been restricted by search config.
            this.resultRowsLimitedMessage = 'cs.sys.genericSearch.resultRowsLimitedByConfiguration';
            this.maxResultRows = searchResult.maxResultRows;
        }

        console.log( 'Loaded search result for \'' + searchResult.name + '\' (rows '
            + ( searchResult.offset + 1 ) + '..' + ( searchResult.offset + searchResult.limit )
            + ' of ' + searchResult.totalCount + ', with ' + searchResult.columns.length + ' columns).' );
        // for ( let i = 0; i < searchResult.columns.length; i++ ) {
        //    console.log(searchResult.columns[i]);
        // }

        this.columns = cloneDeep( searchResult.columns );
        console.log( 'his-search-result.component#loadPage() #this.columns', this.columns );
        this.configColumns = this.prepareTableConfigColumns();
        // LEGACY: apply hidden cols
        this.applyHiddenCols( this.getTableConfig()?.hiddenCols ?? [] );

        this.numberOfDataColumns = this.columns.length;
        this.numberOfVisibleDataColumns = this.columns.filter( column => column.visible ).length;

        const rows = searchResult.data;
        if ( rows && rows.length > 0 ) {
            // respect explicit definition of id/label column and update if necessary
            this.updateIdAndLabelColumnIdx( rows );
        }
        // set rows and apply columns with updated visibility
        this.rowContext.setRows( rows, this.columns, this.selectedOptionsIdColumn );

        this.firstResultRow = searchResult.offset;
        this.totalRowCount = searchResult.totalCount;
        // currentPageNumber muss hier wohl nicht neu berechnet werden
        // falls doch, dann: this.currentPageNumber = 1 + offset / this.rowsPerPage;
        // this.currentPageNumber = 1 + start / this.rowsPerPage;
        this.totalNumberOfPages = Math.ceil( this.totalRowCount / this.rowsPerPage );

        console.log( 'his-search-result.component#loadPage() #result #firstResultRow', this.firstResultRow );
        console.log( 'his-search-result.component#loadPage() #result #currentPageNumber', this.currentPageNumber );
        console.log( 'his-search-result.component#loadPage() #result #rowsPerPage', this.rowsPerPage );

        // Update the selection of the current page used by p-Table to match the global selection.
        if ( this.selectedOptions && this.selectedOptions.length > 0 && this.rowContext.selectedRowKeySet().size === 0 ) {
            // backwards compatibility for use as a subcomponent, ex. search-select-popup, where initial selection is provided by selectedOptions input;
            const selectedRows = this.toSelectedRows( this.selectedOptions );
            this.rowContext.setSelectedRows( selectedRows );
        } else {
            this.rowContext.updateActivePageSelection();
        }

        // why? configure does nothing
        this.columns = this.configureColumnMetadata( this.columns, this.searchConfigFile );
        this.rowContext.rows.forEach(
            ( row, index ) => this.configureRow( start + index, row, this.columns, this.searchConfigFile )
        );

        this.idColumnIndex = this.columns.findIndex( column => column.id === this.idColumn );

        if ( sortField ) {
            this.rowContext.rows.sort( ( r1: HisSearchResultRow, r2: HisSearchResultRow ) => {
                let v1 = r1.getAt( sortField as any as number );
                let v2 = r2.getAt( sortField as any as number );
                let c: number;
                if ( v1 > v2 ) {
                    c = 1;
                } else if ( v1 < v2 ) {
                    c = -1;
                } else {
                    c = 0;
                }
                return ( sortOrder > 0 ) ? c : -c;
            } );
        }

        // selectedOptionsIdColumn should have been set by setSelectedOptionsIdColumn() before;
        // if that did not happen we try to guess the column name and log a warn message.
        if ( !this.selectedOptionsIdColumn ) {
            this.selectedOptionsIdColumn = searchResult.name + '.id';
            console.warn(
                'HisSearchResultComponent.loadPage(): Set selectedOptionsIdColumn = ' + this.selectedOptionsIdColumn );
        }
        this.initialLoadFinished = true;
        this.finishedInitialLoad.emit();
        this.rowsChanged.emit( this.rowContext.rows );
        this.restoreFocusAfterSort();

        if ( this.currentPageNumber === 0 ) {
            this.currentPageNumber = 1;
        }
        this.applyFrozenCols();
    }

    private prepareTableConfigColumns() {
        const configColumns: HisSearchResultConfigColumn[] = [];
        if ( this.rowAction && this.showActionsBefore ) configColumns.push( new HisSearchResultConfigColumn( this.ACTIONS_BEFORE, 'common.actions.short' ) );
        configColumns.push( ...this.columns.filter( c => c.visible === true ).map( c => new HisSearchResultConfigColumn( c.id, c.label ?? c.id ) ) );
        if ( this.rowAction && this.showActionsAfter ) configColumns.push( new HisSearchResultConfigColumn( this.ACTIONS_AFTER, 'common.actions' ) );
        return configColumns;
    }

    private applyHiddenCols( hiddenCols: string[] ) {
        const hiddenColsSet = new Set( hiddenCols ?? [] );
        this.columns.filter( c => hiddenColsSet.has( c.id ) ).forEach( c => c.visible = false );
        this.showActionsBefore = !hiddenColsSet.has( this.ACTIONS_BEFORE );
        this.showActionsAfter = !hiddenColsSet.has( this.ACTIONS_AFTER );
    }

    private updateIdAndLabelColumnIdx( rows: HisSearchResultRow[] ) {
        const probeRow = rows[0];
        // if there is no 'selectedOptionsIdColumn' or it doesnt resolve to a column on the probeRow, we use the default;
        // else if both idx match, we also dont have to update
        const selectedOptionsIdColumnIdx = probeRow.getIndex( this.selectedOptionsIdColumn ) ?? -1;
        const hasCorrectIdIdx = selectedOptionsIdColumnIdx === -1 || probeRow.idIdx === selectedOptionsIdColumnIdx;

        const selectedOptionsLabelColumnIdx = probeRow.getIndex( this.selectedOptionsLabelColumn ) ?? -1;
        const hasCorrectLabelIdx = selectedOptionsLabelColumnIdx === -1 || probeRow.labelIdx === selectedOptionsLabelColumnIdx;

        // set the id column index used by the html template
        this.idColumnIndex = hasCorrectIdIdx ? probeRow.idIdx : selectedOptionsIdColumnIdx;

        if ( hasCorrectIdIdx && hasCorrectLabelIdx ) {
            // if are already the correct index we can stop here
            return;
        }

        rows.forEach( row => {
            if ( !hasCorrectIdIdx ) {
                row.idIdx = selectedOptionsIdColumnIdx;
            }
            if ( !hasCorrectLabelIdx ) {
                row.labelIdx = selectedOptionsLabelColumnIdx;
            }
        } );
    }

    /**
     * Prepares navigation context by loading entry IDs and generating the navigation URL.
     * @returns Observable with navigation context
     */
    public prepareNavigationContext(): Observable<DialogHeaderContext> {
        // Retrieve the 'search' query parameter from the current route
        const { search: searchPath } = this.route.snapshot.queryParams;

        // Return an empty response if the newSearchUrl is missing
        if ( !searchPath ) {
            return of( { ids: [], changeSearchUrl: '', newSearchUrl: '' } );
        }

        // Create URL objects from the current URL and the new search URL
        const currentUrl = new URL( this.router.url, window.location.origin );
        const newSearchUrl = new URL( searchPath, window.location.origin );

        // Add relevant query parameters to the URL for new search
        this.addRequiredParamsToUrl( currentUrl, newSearchUrl );

        // Build the URL with query params to use on change search
        const changeSearchUrl = this.buildChangeSearchUrl( searchPath );

        // If entry IDs are already loaded, return them directly with the updated URLs
        if ( this.entriesIds.length > 0 ) {
            return of( {
                ids: this.entriesIds,
                changeSearchUrl: changeSearchUrl,
                newSearchUrl: newSearchUrl.toString()
            } );
        }

        // If entry IDs are not loaded, load them and return the result
        return this.loadEntryIds().pipe(
            map( entryIds => ( {
                ids: entryIds,
                changeSearchUrl: changeSearchUrl,
                newSearchUrl: newSearchUrl.toString()
            } ) )
        );
    }

    // Helper function to add required parameters to the new URL
    private addRequiredParamsToUrl( currentUrl: URL, newUrl: URL ): void {
        const requiredParams = ['navigationPosition', 'configFile', 'pageTitle'];

        requiredParams.forEach( param => {
            const paramValue = currentUrl.searchParams.get( param );
            if ( paramValue ) {
                newUrl.searchParams.set( param, paramValue );
            }
        } );
    }

    /**
     * Builds the navigation URL by removing filter criteria from query parameters
     * @returns The clean navigation URL string
     */
    private buildChangeSearchUrl( newSearchUrl: string ): string {
        const url = new URL( newSearchUrl, window.location.href );
        const cleanParams = this.getCleanQueryParams();

        const urlTree = this.router.createUrlTree( [url.pathname], {
            queryParams: cleanParams,
            queryParamsHandling: 'merge'
        } );

        return url.origin + this.router.serializeUrl( urlTree );
    }

    /**
     * Removes filter criteria from the current query parameters
     * @returns Clean query parameters object
     */
    private getCleanQueryParams(): Params {
        return FilterUtils.nullAllFilterParams( this.queryParams );
    }

    /**
     * Loads search results and extracts entry IDs
     * @returns Observable with array of entry IDs
     */
    private loadEntryIds(): Observable<any[]> {
        return this.searchService.loadSearchResult( this.search, 0, this.totalRowCount ).pipe(
            map( ( searchResult: HisSearchResult ) => {
                if ( searchResult.data.length === 0 ) {
                    return [];
                }

                // Extract and store entry IDs
                this.entriesIds = searchResult.data
                    .map( entry => entry.get( this.selectedOptionsIdColumn ) );

                return this.entriesIds;
            } )
        );
    }

    restoreFocusAfterSort() {
        setTimeout( () => {
            let clickedHeaderCell = document.querySelectorAll( '.p-sortable-column.p-highlight' )[0];
            if ( clickedHeaderCell !== undefined && clickedHeaderCell.id ) {
                let clickedHeaderCellId = clickedHeaderCell.id;
                let sortedHeaderCell: any = document.getElementById( clickedHeaderCellId );
                sortedHeaderCell.focus();
            }
        }, 1 );
    }

    // Configure

    configureColumnMetadata( columns: HisColumnMetadata[], searchConfigFile: string ): HisColumnMetadata[] {
        return columns;
    }
    configureRow( index: number, row: HisSearchResultRow, columns: HisColumnMetadata[], searchConfigFile: string ): HisSearchResultRow {
        return row;
    }

    newSearch() {
        // navigate back to search page passing only the basic queryParams, no search constraints
        let queryParams: any = {};
        if ( this.navigationPosition ) { queryParams.navigationPosition = this.navigationPosition; }
        if ( this.searchConfigFile ) { queryParams.configFile = this.searchConfigFile; }
        if ( this.pageTitle ) { queryParams.pageTitle = this.pageTitle; }
        if ( this.tableTitle ) { queryParams.tableTitle = this.tableTitle; }
        // it feels correct to keep the toggle state as well if we click on "Neue Suche"
        if ( this.queryParams.extended ) { queryParams.extended = true; }

        let route = '../search';
        console.log( 'HisSearchResultComponent.newSearch(): Current route:', this.route.url );
        console.log( 'HisSearchResultComponent.newSearch(): Navigating to \'' + route + '\' with queryParams', queryParams );

        sessionStorage.setItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON, 'NEW_SEARCH' );

        this.router.navigate( [route], {
            relativeTo: this.route,
            queryParams
        } );
    }

    /**
     * Generates a URL without filter parameters based on the provided route and query parameters.
     * This method encapsulates the logic for creating a URL tree, merging query parameters, and serializing the URL.
     *
     * @param route - The target route for navigation (e.g., '../search').
     * @param params - The query parameters to be included in the URL.
     * @returns The generated URL as a string.
     */
    private generateUrlWithoutFilterParams( route: string, params: any ): string {
        let urlTree = this.router.createUrlTree( [route], {
            relativeTo: this.route,
            queryParams: params,
            queryParamsHandling: 'merge'
        } );

        let urlString = this.router.serializeUrl( urlTree );
        let urlBasePath = this.navigationService.getUrlBasePath();

        return urlBasePath + urlString;
    }

    changeSearch() {
        // navigate back to search page passing all queryParams
        let route = '../search';
        console.log( 'HisSearchResultComponent.changeSearch(): Current route:', this.route.url );
        console.log( 'HisSearchResultComponent.changeSearch(): Navigating to \'' + route + '\' with queryParams', this.queryParams );

        sessionStorage.setItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON, 'CHANGE_SEARCH' );

        if ( this.toggleTableFilter ) {
            let params = FilterUtils.nullAllFilterParams( this.queryParams );
            let urlWithoutFilterParams = this.generateUrlWithoutFilterParams( route, params );
            console.log( 'his-search-result.component#changeSearch() #params #urlWithoutFilterParams', urlWithoutFilterParams );

            if ( !this.ignoreForBackToSearchResultNavigation ) {
                // ohne diesen Aufruf manifestirt sich die neue URL nicht
                this.navigationService.setSearchResultUrl( urlWithoutFilterParams );
            }

            this.router.navigate( [route], {
                relativeTo: this.route,
                queryParams: params
            } );
            return;
        }

        this.router.navigate( [route], {
            relativeTo: this.route,
            queryParams: this.queryParams
        } );
    }

    /**
     * Delete the row from the result table at the given index.
     *
     * Actually we do not delete the result row blindly but reload the page;
     * so only if the object has really been deleted in the database it will be gone.
     *
     * @param index the relative row index, i.e. 0 if we want to delete the first element on page 2
     *
     * @deprecated because the method name is misleading and the index argument is not required at all; use reloadPageAfterDelete() instead
     */
    deleteRow( index: number ) {
        this.reloadPageAfterDelete();
    }

    /**
     * Reload the current search result page after one of its rows has been deleted.
     *
     * @param deletedRows - the rows that have been deleted and should be deselected after the reload;
     * if not provided, no rows will be deselected, which can lead to an inconsistent state of the selection if the deleted row(s) were part of the current selection
     */
    reloadPageAfterDelete(deletedRows: SelectableRow[] = []) {
        if ( this.firstResultRow + 1 === this.totalRowCount && this.firstResultRow > 0 ) {
            // we are on the last page which has only one result row and there are other pages -> jump one page back
            this.firstResultRow -= this.rowsPerPage;
        }

        // We have to reload the page to get 10 result rows again if there are >= 10 in total.
        // Reloading the page updates total row count, current page number etc. as well.
        console.log( 'Reload after delete: firstResultRow =', this.firstResultRow, ', rowsPerPage =', this.rowsPerPage );
        this.loadPage( this.firstResultRow );
        this.updateSortIconAfterDeletion();
        this.deselectRows( deletedRows );
    }

    private updateSortIconAfterDeletion() {
        setTimeout( () => {
            let clickedHeaderCell = document.querySelectorAll( '.p-sortable-column.p-highlight' )[0];
            if ( clickedHeaderCell !== undefined ) {
                let iconElement = clickedHeaderCell.querySelector( '.p-sortable-column-icon' );
                if ( iconElement !== undefined ) {
                    let currentClass = Array.from( iconElement.classList ).find( cls => cls.startsWith( 'pi-sort-amount-' ) );
                    if ( currentClass ) {
                        iconElement.classList.remove( currentClass );
                    }
                    iconElement.classList.add( 'pi-sort-alt' );
                }
            }
        }, 100 );
    }

    private showJobStarter(): boolean {
        if ( !this.jobStarterKey ) {
            return false;
        }

        return this.authService.hasAll( HisJobStarterComponent.jobRight );
    }

    // unused stub
    getRowActionDefaultIcon( action: string | any ) {
        if ( !isString( action ) ) { action = action.action; }
        switch ( action ) {
            case 'edit': return 'edit';
            case 'add': return 'plus';
            case 'delete': return 'close';
            default: return 'question';
        }
    }

    // unused stub
    getColumnStyle( column: HisColumnMetadata ): any {
        return {
            width: ( !column.width && column.dataType === 'checkbox' ) ? '34px' : column.width
        };
    }

    // unused stub
    getRadioButtonGroup( columnIndex: number ): string {
        let group = this.radioButtonGroups['' + columnIndex];
        if ( !group ) {
            group = this.baseGlobalService.uid();
            console.log( columnIndex + ':' + group );
            this.radioButtonGroups['' + columnIndex] = group;
        }
        return group;
    }

    rowsPerPageChanged( value: number | null ) {
        console.log( 'his-search-result.component#refreshTable() #value', value );
        console.log( 'his-search-result.component#refreshTable() #this.rowsPerPage', this.rowsPerPage );

        // emulate table config change behavior for external influence (#resultListTemplate)
        this.onTableConfigChanged( { tablePageSize: { current: value, previous: this.rowsPerPage } } );
    }

    private getCurrentSortOrder() {
        const currentSearchCriteria = HisSearchResultColumnSortHelper.getSafedSortCriteria();

        return currentSearchCriteria?.length > 0
            ? ( currentSearchCriteria[0].ascending ? 1 : -1 )
            : null;
    }

    private getCurrentSortField() {
        const currentSearchCriteria = HisSearchResultColumnSortHelper.getSafedSortCriteria();

        return currentSearchCriteria?.length > 0 ? currentSearchCriteria[0].field : null;
    }

    // *** Filter result table ***
    toggleFilter() {
        this.toggleTableFilter = !this.toggleTableFilter;
        this.filterToggled.emit( this.toggleTableFilter );

        console.log( 'his-search-result.component#onToggleFilter() #this.toggleTableFilter', this.toggleTableFilter );

        // Comparison with JSF: Filter is reset on close
        if ( this.toggleTableFilter === false ) {
            this.onResetFilter();
        }

        this.tableService.setTableConfigValue( this.userProfileKeyBasePath, 'showTableFilter', this.toggleTableFilter ? true : null );
    }

    /** Writes every entry at filter inputs to this.filter */
    onFilter( value: string, column: HisColumnMetadata ) {
        // console.log('his-search-result.component#onFilter() #column.id', column.id);
        // console.log('his-search-result.component#onFilter() #value', value);
        // console.log('his-search-result.component#onFilter() #columnsToFilter', this.filter);
        this.filter[column.filterPath] = value;
    }

    /**
     * NOTE: Standard filter, this.searchResultTable.filter('value', 'field', 'matchMode'), does not work on all search results
     */
    onFilterTable() {
        this.notificationService.clear();

        // filterGroup an url übergeben Anfang
        // If this is not a popup search then register the current url for "back to search result" button
        if ( !this.isSearchPopup ) {
            const searchWithoutDefaultGroup = HisSearchParserHelper.removeDefaultGroupFromSearchIfOtherGroupIsPresent( cloneDeep( this.search ) );
            const searchParams = HisSearchParserHelper.convertHisSearchToFlatObjectFormatParams( searchWithoutDefaultGroup );
            const filterParams = FilterUtils.convertFilterToParams( this.filter );
            this.updateParamsAndSetNewUrlToBrowser( { ...searchParams, ...filterParams } );
        }
        // filterGroup an url übergeben Ende

        console.log( 'his-search-result.component#onFilterTable() #filter', this.filter );
        this.loadPage(
            0,
            this.search,
            undefined,
            undefined,
            undefined,
            FilterUtils.toFilterCrtieria( this.filter )
        );
    }

    onResetFilter() {
        this.notificationService.clear();
        console.log( 'his-search-result.component#onResetFilter() #this.filter', this.filter );

        // reset input values
        this.filter = {};

        // Filter-Kriterium aus URL löschen
        const newUrlParams = FilterUtils.nullAllFilterParams( cloneDeep( this.queryParams ) );
        console.log( 'his-search-result.component#onResetFilter() #newUrlParams', newUrlParams );

        this.updateParamsAndSetNewUrlToBrowser( newUrlParams );

        // loadPage with default values, and the stored sortCriteria to keep the original sort order
        this.loadPage( 0, this.search, undefined, 0, HisSearchResultColumnSortHelper.getSafedSortCriteria() );
    }

    private updateParamsAndSetNewUrlToBrowser( searchParams: Params ) {
        // schreibt die params in die auf der Suchergebnisseite angezeigte Url
        // wäre nicht unbedingt notwendig, stellt aber konsistenten Zustand her:
        // * Hinzufügen beim Filter anwenden
        // * entfernen beim Filter ausblenden
        this.router.navigate( [], {
            relativeTo: this.route,
            queryParams: searchParams,
            queryParamsHandling: 'merge'
        } );

        let urlTree = this.router.createUrlTree( [], {
            relativeTo: this.route,
            queryParams: searchParams,
            queryParamsHandling: 'merge'
        } );

        let urlString = this.router.serializeUrl( urlTree );
        let urlBasePath = this.navigationService.getUrlBasePath();
        console.log( 'his-search-result.component#onFilterTable() #url #shortUrl', urlBasePath );

        let urlWithFilterParam = urlBasePath + urlString;
        console.log( 'his-search-result.component#onFilterTable() #url #urlWithFilterParam', urlWithFilterParam );

        if ( !this.ignoreForBackToSearchResultNavigation ) {
            // ohne diesen Aufruf manifestirt sich die neue URL nichtnicht
            this.navigationService.setSearchResultUrl( urlWithFilterParam );
        }
    }

    /** @deprecated */
    reloadPage(evt: any) {
        console.log( 'his-search-result-component#reloadPage() #event', evt );
        const pageReloaded = this.onTableConfigChanged();

        if ( !pageReloaded ) {
            this.loadPage(
                this.firstResultRow,
                undefined,
                this.getCurrentSortField(),
                this.getCurrentSortOrder(),
                HisSearchResultColumnSortHelper.getSafedSortCriteria()
            );
        }
    }

    /** @returns true, if a page reload was triggered */
    protected onTableConfigChanged( configChanges?: DevTableConfigChanges ) {
        const config = this.getTableConfig();
        const { changes = configChanges } = config;
        if ( !changes ) {
            return false;
        }

        if ( changes.paginatorPosition ) {
            this.setPaginatorPosition( config.paginatorPosition );
        }

        if ( changes.hiddenCols ) {
            changes.hiddenCols.previous.forEach( colId => this.columns.filter( c => c.id === colId ).map(c => c.visible = true ));
            this.applyHiddenCols( config.hiddenCols );
        }

        if ( changes.frozenCol ) {
            this.frozenCols = config.frozenCol;
            this.applyFrozenCols();
        }

        if ( changes.tablePageSize ) {
            this.currentPageNumber = 1; // changes in pagesize reset to page 1 (same as JSF)
            this.loadPage( 0, undefined, this.getCurrentSortField(), this.getCurrentSortOrder(), HisSearchResultColumnSortHelper.getSafedSortCriteria() );
            return true;
        }
        return false;
    }

    /* set last window.location.href before page leave on defaultRoute navigation */
    public onBeforeNavigateDefaultRoute() {
        this.navigationService.setLastBackUrl();
    }

    private setUserProfileBasePath() {
        if ( this.PATHNAME.includes( 'print-view' ) ) {
            let tableId = this.route.snapshot.queryParams.pathname + this.searchConfigFile;
            let tableIdHash = HisStringUtils.getHashCode( tableId );
            this.userProfileKeyBasePath = tableIdHash;
        } else {
            let tableId = this.PATHNAME + this.searchConfigFile;
            let tableIdHash = HisStringUtils.getHashCode( tableId );
            this.userProfileKeyBasePath = tableIdHash;
        }
    }

    private loadPaginatorPosition() {
        const config = this.getTableConfig();
        const paginatorPos = config.paginatorPosition ?? null;
        this.setPaginatorPosition( paginatorPos );
    }

    private setPaginatorPosition( paginatorPos: Position ) {
        // falls paginatorPos= null, dann this.paginatorPosition = 'bottom' (Default)
        const position = paginatorPos ?? 'bottom';
        this.paginator = true;
        this.paginatorPosition.set( position );
    }

    /** @deprecated */
    public onPaginatorPositionChange( pos: Position ) {
        this.setPaginatorPosition( pos );
    }

    private getTableConfig() {
        return this.tableService.getTableConfig( this.userProfileKeyBasePath );
    }

    /**
     * This method clears the data within the 'rows' and 'columns' arrays.
     */
    clearRowsAndColumns(): void {
        this.rowContext.rows.length = 0;
        this.columns.length = 0;
    }

    showPrintview() {
        let printview = new HisSearchPrintviewData();
        printview.pageTitle = this.pageTitle;
        printview.searchConfigFile = this.searchConfigFile;
        printview.showSearchInfo = this.showSearchInfo;
        printview.queryParams = this.queryParams;
        printview.searchResultSize = this.totalRowCount;

        let modifiedCopyOfQueryParams = { ...this.route.snapshot.queryParams };

        delete modifiedCopyOfQueryParams.pageTitle;
        delete modifiedCopyOfQueryParams.page;
        modifiedCopyOfQueryParams.pathname = this.PATHNAME;

        this.router.navigate( ['/print-view/'], { state: { data: printview }, queryParams: modifiedCopyOfQueryParams } );
    }

    switchListTable() {
        const newMode: ViewMode = this.showList() ? VIEW_MODE.tabelle : VIEW_MODE.liste;

        this.userProfileService.preloadUserProfile().pipe(
            take(1),
            catchError(() => of({} as { [key: string]: string })),
            map((profile: { [key: string]: string }) => !profile || Object.keys(profile).length === 0),
            switchMap((isEmpty: boolean) => {
                if (isEmpty) {
                    this.navigateToMode(newMode);
                    return of(null);
                }

                return this.userProfileService
                    .setNewValue(this.searchResultModeUserProfileKey, newMode, VIEW_MODE.tabelle)
                    .pipe(
                        tap(() => this.navigateToMode(newMode)),
                        switchMap(() => of(null))
                    );
            })
        ).subscribe({
            error: err => console.error('Unerwarteter Fehler im switchListTable Flow:', err)
        });
    }

    private navigateToMode(newMode: ViewMode): void {
        this.router
            .navigate([], {
                relativeTo: this.route,
                queryParams: { mode: newMode, page: this.currentPageNumber },
                queryParamsHandling: 'merge',
                replaceUrl: true
            })
            .then(success => {
                if (success) {
                    this.navigationService.setSearchResultUrl(window.location.href);
                } else {
                    console.warn('Navigation wurde übersprungen oder abgebrochen.');
                }
            })
            .catch(err => console.error('Navigationsfehler:', err));
    }
}
